import { AiModel } from "administration/aiModels/model/AiModel";
import { NativeAiParameter } from "./NativeAiParameter";
import { ParameterEditorValue } from "./ParameterEditorValue";

export interface INativeAiData {
    frendsSystemPrompt?: string;
    currentModelCustomOptions?: ModelCustomOptions;
    defaultFrendsSystemPrompt?: string;
    models?: AiModel[];
    frendsAiServiceEnabled?: boolean;
}

export interface ModelCustomOptions {
    modelId?: number;
    optionsJson: string;
}

export type NativeAiParameterValue = string | ParameterEditorValue | ParameterEditorValue[] | SystemPrompt[] | FileAttachment[];

export const NATIVE_AI_PARAMETER_INPUT_DEFAULT_VALUE: ParameterEditorValue = {
    mode: "text",
    value: "",
    disableWordWrap: false
};

export const parseAsParameterEditorValue = (value?: string | ParameterEditorValue) => {
    if (!value) {
        return NATIVE_AI_PARAMETER_INPUT_DEFAULT_VALUE;
    }

    // backwards compatibility with v6.1.1
    if (typeof value === "string") {
        return {
            ...NATIVE_AI_PARAMETER_INPUT_DEFAULT_VALUE,
            value: value
        };
    }

    return value;
};

export const parseAsParameterEditorValues = (values?: string | ParameterEditorValue[]) => {
    if (!values) {
        return [];
    }

    // backwards compatibility with v6.1.1
    if (typeof values === "string") {
        try {
            const stringValues = JSON.parse(values) as string[];
            return stringValues.map((v) => parseAsParameterEditorValue(v))
        } catch (_) {
            return [];
        }
    }

    return values;
};

export const NATIVE_AI_SYSTEM_PROMPT_DEFAULT_VALUE: SystemPrompt = {
    ...NATIVE_AI_PARAMETER_INPUT_DEFAULT_VALUE,
    frendsSystemPrompt: false,
};

export interface SystemPrompt extends ParameterEditorValue {
    frendsSystemPrompt: boolean;
}

export const parseAsSystemPrompts = (values?: string | SystemPrompt[]) => {
    if (!values) {
        return [];
    }

    // backwards compatibility with v6.1.1
    if (typeof values === "string") {
        try {
            const prompts = JSON.parse(values) as SystemPrompt[];
            return prompts.map((p) => ({
                ...NATIVE_AI_SYSTEM_PROMPT_DEFAULT_VALUE,
                ...p,
            }))
        } catch (_) {
            return [];
        }
    }

    return values;
};

export interface FileAttachment extends ParameterEditorValue {
    id?: string;
    file?: File;
}

export const NATIVE_AI_FILE_ATTACHMENT_DEFAULT_VALUE: FileAttachment = {
    ...NATIVE_AI_PARAMETER_INPUT_DEFAULT_VALUE,
}

export const parseAsFileAttachments = (values?: string | FileAttachment[]) => {
    if (!values) {
        return [];
    }

    // backwards compatibility with v6.1.1
    if (typeof values === "string") {
        try {
            const attachments = JSON.parse(values) as { fileData: string }[];
            return attachments.map((a) => ({
                ...NATIVE_AI_FILE_ATTACHMENT_DEFAULT_VALUE,
                value: a.fileData
            }))
        } catch (_) {
            return [];
        }
    }

    return values;
};

export const FRENDS_AI_SERVICE_TYPE_OPTION = { value: "frendsAi", label: "Frends AI" };
export const AZURE_INFERENCE_SERVICE_TYPE_OPTION = { value: "inference", label: "Azure Inference" };
export const OLLAMA_SERVICE_TYPE_OPTION = { value: "ollama", label: "Ollama" };

export const SERVICE_TYPE_OPTIONS = [
    FRENDS_AI_SERVICE_TYPE_OPTION,
    AZURE_INFERENCE_SERVICE_TYPE_OPTION,
    OLLAMA_SERVICE_TYPE_OPTION
];

export const NATIVE_AI_FLOAT_PARAMETER_MIN = 0.1;
export const NATIVE_AI_FLOAT_PARAMETER_MAX = 1.0;
export const NATIVE_AI_FLOAT_PARAMETER_STEP = 0.1;

export const OPTIONS_JSON_WITH_DEFAULT_TOP_P_AND_TEMP_VALUES = JSON.stringify({
    [NativeAiParameter.OptionsJson_TopP]: NATIVE_AI_FLOAT_PARAMETER_MIN.toString(),
    [NativeAiParameter.OptionsJson_Temperature]: NATIVE_AI_FLOAT_PARAMETER_MIN.toString()
});

export const OPTIONS_JSON_EMPTY_VALUE = "{\n\t\n}";

